// ./src/components/features/Timeline/TimelineItemsList/TimelineItemsList.tsx
//
// Event timeline items list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit / Delete only), rights-gated per row, selection-ready.
// Each row shows the phase, the scheduled time range (in the event's timezone),
// who is responsible, and any vendor / venue involved. Category name + icon/color
// come from the shared CategoriesContext. Mirrors BudgetItemsList (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
  Badge,
} from "@mantine/core";

import { TimelineItem } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonCheckbox, CardSkeletonLines, countCardActions, type CardSkeletonShape } from "@/components/generic";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

import { getPhaseMeta, getPartyKindIcon, formatTimelineWhen, isTimelinePast } from "../timelineHelpers";

export interface TimelineItemsListProps {
  data?: TimelineItem[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditItem?: (item: TimelineItem) => void;
  onDeleteItem?: (item: TimelineItem) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddItem?: () => void;
  /** IANA timezone of the event; the time range is rendered in this zone. */
  eventTimezone?: string | null;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search / filter is narrowing the list. An empty result then means "no
   * match" — NOT "nothing here yet", so show that instead of the Add prompt.
   */
  hasQueryNarrowing?: boolean;
}

interface CategoryMeta {
  name: string | null;
  icon: string | null;
  color: string | null;
}

const PhaseBadge: React.FC<{ phase?: string | null }> = ({ phase }) => {
  const meta = getPhaseMeta(phase);

  return (
    <Badge variant="light" color={meta.color} size="sm" radius="sm">
      {meta.label}
    </Badge>
  );
};

const CategoryCell: React.FC<{ meta: CategoryMeta | null }> = ({ meta }) => {
  if (!meta?.name) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      {meta.icon ? <AppIcon iconName={meta.icon} size={16} color={meta.color || undefined} /> : null}
      <Text size="md" c={meta.color || undefined} lineClamp={1}>
        {meta.name}
      </Text>
    </Group>
  );
};

const ResponsibleCell: React.FC<{ item: TimelineItem }> = ({ item }) => {
  if (!item.responsibleLabel) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <AppIcon iconName={getPartyKindIcon(item.responsibleKind)} size={15} />
      <Text size="md" lineClamp={1}>
        {item.responsibleLabel}
      </Text>
    </Group>
  );
};

const InvolvedCell: React.FC<{ item: TimelineItem }> = ({ item }) => {
  if (!item.vendorLabel && !item.venueLabel) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Stack gap={2}>
      {item.vendorLabel ? (
        <Group gap={6} wrap="nowrap">
          <AppIcon iconName="Briefcase" size={14} />
          <Text size="md" lineClamp={1}>
            {item.vendorLabel}
          </Text>
        </Group>
      ) : null}
      {item.venueLabel ? (
        <Group gap={6} wrap="nowrap">
          <AppIcon iconName="MapPin" size={14} />
          <Text size="md" lineClamp={1}>
            {item.venueLabel}
          </Text>
        </Group>
      ) : null}
    </Stack>
  );
};

const WhenCell: React.FC<{ item: TimelineItem; tz?: string | null }> = ({ item, tz }) => {
  const when = formatTimelineWhen(item.startsAt, item.endsAt, tz);

  if (!when) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap" align="flex-start">
      <Box mt={2} style={{ flexShrink: 0, display: "flex" }}>
        <AppIcon iconName="Clock" size={14} />
      </Box>
      <Box style={{ minWidth: 0 }}>
        <Text size="md" fw={500} style={{ whiteSpace: "nowrap" }}>
          {when.date}
        </Text>
        <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
          {when.time}
        </Text>
      </Box>
    </Group>
  );
};

const PassedBadge: React.FC = () => (
  <Badge variant="light" color="gray" size="sm" radius="sm">
    Passed
  </Badge>
);

const ItemIcon: React.FC<{ item: TimelineItem; meta: CategoryMeta | null; size?: number }> = ({ item, meta, size = 34 }) => (
  <ThemeIcon variant="light" color={meta?.color || getPhaseMeta(item.phase).color} radius="xl" size={size}>
    <AppIcon iconName={meta?.icon || getPhaseMeta(item.phase).icon} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  item: TimelineItem;
  onEdit?: (i: TimelineItem) => void;
  onDelete?: (i: TimelineItem) => void;
  isDeleting: boolean;
}> = ({ item, onEdit, onDelete, isDeleting }) => {
  const showEdit = !!onEdit && recordHasRight(item, "edit");
  const showDelete = !!onDelete && recordHasRight(item, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label="Edit" withArrow position="bottom">
          <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onEdit(item)} disabled={isDeleting} aria-label="Edit">
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label="Delete" withArrow position="bottom">
          <ActionIcon variant="subtle" color="red" size="md" onClick={() => onDelete(item)} loading={isDeleting} disabled={isDeleting} aria-label="Delete">
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  item: TimelineItem;
  meta: CategoryMeta | null;
  tz?: string | null;
  isPast: boolean;
  onEdit?: (i: TimelineItem) => void;
  onDelete?: (i: TimelineItem) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

const TimelineTableRow: React.FC<RowProps> = ({
  item,
  meta,
  tz,
  isPast,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => (
  <Table.Tr
    style={{
      verticalAlign: "top",
      opacity: isDeleting ? 0.5 : isPast ? 0.6 : 1,
      backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        {selectable && (
          <Checkbox
            mt={7}
            checked={selected}
            onChange={() => item.id && onToggleSelect?.(item.id)}
            disabled={isDeleting}
            aria-label={item.name || "Select"}
          />
        )}
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ItemIcon item={item} meta={meta} />
        <Box style={{ minWidth: 0 }}>
          <Text size="md" fw={600} lineClamp={1}>
            {item.name || "—"}
          </Text>
          <Group gap={6} mt={4} wrap="nowrap">
            <PhaseBadge phase={item.phase} />
            {isPast ? <PassedBadge /> : null}
          </Group>
        </Box>
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 190 }}>
      <WhenCell item={item} tz={tz} />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <CategoryCell meta={meta} />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <ResponsibleCell item={item} />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <InvolvedCell item={item} />
    </Table.Td>
    <Table.Td style={{ width: 90 }}>
      <RowActions item={item} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
    </Table.Td>
  </Table.Tr>
);

const TimelineTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 190 }}>
      <Skeleton height={14} width={150} />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <Skeleton height={14} width={100} />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={14} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 90 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const TimelineCardComponent: React.FC<CardProps> = ({
  item,
  meta,
  tz,
  isPast,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const toggleSelect = () => item.id && onToggleSelect?.(item.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(item, "edit")) {
      actions.push({ key: "edit", icon: "Edit", label: "Edit", color: "blue", onClick: () => onEdit(item), disabled: isDeleting });
    }

    if (onDelete && recordHasRight(item, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: "Delete", color: "red", onClick: () => onDelete(item), loading: isDeleting, disabled: isDeleting });
    }

    return actions;
  }, [onEdit, onDelete, item, isDeleting]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : isPast ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox mt={2} checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={item.name || "Select"} />
        )}
        <ItemIcon item={item} meta={meta} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            <Text size="md" fw={600} lineClamp={1}>
              {item.name || "—"}
            </Text>
            <Group gap={6} wrap="nowrap">
              {isPast ? <PassedBadge /> : null}
              <PhaseBadge phase={item.phase} />
            </Group>
          </Group>
          <Box mt={4}>
            <WhenCell item={item} tz={tz} />
          </Box>
          <Box mt={4}>
            <CategoryCell meta={meta} />
          </Box>
          {item.responsibleLabel ? (
            <Box mt={2}>
              <ResponsibleCell item={item} />
            </Box>
          ) : null}
          {item.vendorLabel || item.venueLabel ? (
            <Box mt={2}>
              <InvolvedCell item={item} />
            </Box>
          ) : null}
        </Box>
        {showActions && <RowActions item={item} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const TimelineCard = React.memo(TimelineCardComponent);

// Name + phase badge, the date over its time (xs), the category, and the responsible person — most run-of-show items
// name who does them (ROADMAP #127). A vendor / venue line is rarer and left out. Measured with a category: 163px against 160.
const TimelineCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={5} sizes={['md', 'md', 'xs', 'md', 'md']} gap={3} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const TimelineItemsListComponent: React.FC<TimelineItemsListProps> = ({
  data = [],
  loading = false,
  isLoaded,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onEditItem,
  onDeleteItem,
  isRemoving = () => false,
  highlightedId,
  onAddItem,
  eventTimezone,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { isDesktop } = useAppEnvironment();
  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditItem, onDeleteItem);
  const categoriesContext = useOptionalCategoriesContext();

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const categoryMeta = useCallback(
    (item: TimelineItem): CategoryMeta | null => {
      if (!item.categoryId) {
        return null;
      }

      const category = categoriesContext?.getById(item.categoryId);

      if (!category) {
        return null;
      }

      return { name: category.name || category.code || null, icon: category.icon ?? null, color: category.color ?? null };
    },
    [categoriesContext]
  );

  const handleEdit = useCallback(
    (item: TimelineItem) => {
      closeAllCards();
      onEditItem?.(item);
    },
    [onEditItem, closeAllCards]
  );

  const handleDelete = useCallback(
    (item: TimelineItem) => {
      closeAllCards();
      onDeleteItem?.(item);
    },
    [onDeleteItem, closeAllCards]
  );

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          No timeline items match your search.
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title="No timeline items yet"
          description="Map out your run-of-show — what needs to happen before, during and after the event, when, and who's responsible."
          iconName="Timeline"
          actionLabel="Add timeline item"
          onAction={onAddItem}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (item: TimelineItem): boolean =>
    isSelectable && ((!!onEditItem && recordHasRight(item, "edit")) || (!!onDeleteItem && recordHasRight(item, "remove")));

  const rowProps = (item: TimelineItem): RowProps => ({
    item,
    meta: categoryMeta(item),
    tz: eventTimezone,
    isPast: isTimelinePast(item.startsAt, item.endsAt, eventTimezone),
    onEdit: onEditItem ? handleEdit : undefined,
    onDelete: onDeleteItem ? handleDelete : undefined,
    highlighted: item.id === highlightedId,
    isDeleting: isRemoving(item.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(item),
    selected: selection?.isSelected(item.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = data
    .filter((item) => rowIsSelectable(item))
    .map((item) => item.id)
    .filter((id): id is string => !!id);

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label="Select all"
                    />
                  </Table.Th>
                )}
                <Table.Th>Item</Table.Th>
                <Table.Th style={{ width: 190 }}>When</Table.Th>
                <Table.Th style={{ width: 150 }}>Category</Table.Th>
                <Table.Th style={{ width: 160 }}>Responsible</Table.Th>
                <Table.Th style={{ width: 180 }}>Vendor / Venue</Table.Th>
                <Table.Th style={{ width: 90 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => <TimelineTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : data.map((item) => <TimelineTableRow key={item.id} {...rowProps(item)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <TimelineCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((item) => (
          <TimelineCard
            key={item.id}
            {...rowProps(item)}
            forceClose={shouldForceClose(item.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(item.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(item.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const TimelineItemsList = React.memo(TimelineItemsListComponent);
